import { useEffect, useState } from "react";
import { get } from "@/utils/service";

/**
 * Real totals for a statistics strip: each entry asks the list endpoint for one row and reads
 * the total from its pagination, so the numbers always match the list (visibility included).
 */
export function useCounts(url: string, queries: Record<string, Record<string, string | undefined>>, refreshKey = 0) {
  const [counts, setCounts] = useState<Record<string, number | null>>({});
  const [loading, setLoading] = useState(true);
  const key = JSON.stringify(queries);

  useEffect(() => {
    let live = true;
    setLoading(true);
    const entries = Object.entries(JSON.parse(key) as Record<string, Record<string, string | undefined>>);
    Promise.all(entries.map(([name, params]) =>
      get<{ pagination?: { total: number } }>(url, { ...params, page_size: 1 })
        .then((r) => [name, r.pagination?.total ?? null] as const)
        .catch(() => [name, null] as const)))
      .then((res) => { if (live) setCounts(Object.fromEntries(res)); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [url, key, refreshKey]);

  return { counts, loading };
}

/** "1,234", or "—" while unknown. */
export const countText = (n: number | null | undefined) => (n === null || n === undefined ? "—" : n.toLocaleString());

/** One GET for a statistics strip (e.g. /invoices/stats); null until loaded or when not allowed. */
export function useApiData<T>(url: string, enabled = true, refreshKey = 0) {
  const [data, setData] = useState<T | null>(null);
  useEffect(() => {
    if (!enabled) return;
    let live = true;
    get<{ data: T }>(url).then((r) => { if (live) setData(r.data); }).catch(() => { if (live) setData(null); });
    return () => { live = false; };
  }, [url, enabled, refreshKey]);
  return data;
}
